/**
 * Team Approvals Tab Component
 * Shows team members' approval activity
 */
import React from 'react';
import { 
  Users, 
  Clock, 
  CheckCircle2,
  User,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';

export default function TeamApprovalsTab({ teamActivity, isLoading }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 animate-pulse">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#2F2F2F] rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-[#2F2F2F] rounded w-2/3" />
                <div className="h-3 bg-[#2F2F2F] rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  const activity = teamActivity?.teamActivity || [];
  
  if (activity.length === 0) {
    return (
      <div className="text-center py-16 bg-[#111111] border border-[#1F1F1F] rounded-xl">
        <Users size={48} className="mx-auto text-[#2F2F2F] mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Team Members</h3>
        <p className="text-sm text-[#6B6B6B]">Team activity will appear here once there are other users.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-blue-400" />
            <span className="text-xs text-[#6B6B6B]">Team Size</span>
          </div>
          <p className="text-2xl font-bold text-white">{activity.length}</p>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-amber-400" />
            <span className="text-xs text-[#6B6B6B]">Total Pending</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {activity.reduce((sum, m) => sum + (m.pendingCount || 0), 0)}
          </p>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={16} className="text-emerald-400" />
            <span className="text-xs text-[#6B6B6B]">Total Approved</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {activity.reduce((sum, m) => sum + (m.approvedCount || 0), 0)}
          </p>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-purple-400" />
            <span className="text-xs text-[#6B6B6B]">Avg. Per Member</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {Math.round(activity.reduce((sum, m) => sum + (m.approvedCount || 0), 0) / Math.max(activity.length, 1))}
          </p>
        </div>
      </div>
      
      {/* Team Member Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activity.map((member, idx) => (
          <TeamMemberCard key={member.userId || idx} member={member} />
        ))}
      </div>
    </div>
  );
}

function TeamMemberCard({ member }) {
  const hasHighPending = member.pendingCount > 5;
  
  const levelLabels = {
    1: 'Data Owner',
    2: 'Manager',
    3: 'ESG Head'
  };
  
  return (
    <div className={`
      bg-[#111111] border rounded-xl p-4 transition-all
      ${hasHighPending ? 'border-amber-500/30' : 'border-[#1F1F1F]'}
    `}>
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-medium text-sm">
          {member.userName?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-white truncate">{member.userName}</h4>
          <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
            <span>{member.userRole || 'Team Member'}</span>
            <span className="text-[#3F3F3F]">|</span>
            <span>Level {member.approvalLevel}</span>
          </div>
        </div>
        
        {/* Warning if high pending */}
        {hasHighPending && (
          <AlertTriangle size={16} className="text-amber-400" />
        )}
      </div>
      
      {/* Stats */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#1F1F1F]">
        <div className="flex items-center gap-1.5">
          <Clock size={12} className={hasHighPending ? 'text-amber-400' : 'text-[#6B6B6B]'} />
          <span className={`text-sm font-medium ${hasHighPending ? 'text-amber-400' : 'text-white'}`}>
            {member.pendingCount || 0}
          </span>
          <span className="text-xs text-[#6B6B6B]">pending</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle2 size={12} className="text-emerald-400" />
          <span className="text-sm font-medium text-white">{member.approvedCount || 0}</span>
          <span className="text-xs text-[#6B6B6B]">approved</span>
        </div>
      </div>
    </div>
  );
}
